import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { shouldRegisterServiceWorker } from '@player/domain';
import { App } from './App.js';
import { compose, APP_VERSION } from './composition.js';

const container = document.getElementById('root');
if (!container) throw new Error('main: manca #root');

/**
 * Un guasto di avviamento si **scrive sullo schermo**, non solo nella console.
 *
 * `void compose().then(…)` lasciava la promessa rifiutata a se stessa: schermo bianco, e il motivo
 * — per esempio un pacchetto compilato senza sapere dove chiedere un codice di attivazione —
 * leggibile soltanto aprendo gli strumenti di sviluppo. Su un televisore quegli strumenti non
 * esistono, quindi il messaggio non lo legge nessuno.
 *
 * È volutamente fuori dall'i18n: non è testo di prodotto, è un guasto di compilazione o di
 * configurazione, e lo legge chi distribuisce.
 */
function showBootFailure(error: unknown): void {
  const message = error instanceof Error ? error.message : String(error);
  const pre = document.createElement('pre');
  pre.style.cssText =
    'height:100%;margin:0;padding:40px;background:#0A0D13;color:#FF6B61;' +
    'font:500 18px ui-monospace,Menlo,monospace;white-space:pre-wrap';
  pre.textContent = message;
  container!.replaceChildren(pre);
}

/**
 * Il service worker, **dove l'app è un sito**.
 *
 * ## Perché la regola non è qui
 *
 * Sta in `shouldRegisterServiceWorker`, nel dominio, perché i casi che deve coprire su questa
 * macchina non si possono mettere in piedi: il `file://` di un televisore Samsung e il loopback con
 * cui l'APK si serve da sé. Là si provano; qui c'è solo il mestiere di registrare.
 *
 * ## La versione nell'indirizzo
 *
 * `sw.js?v=0.3.3` non è un trucco per svuotare la cache: è **come** il service worker sa di che
 * versione è, visto che `public/` Vite lo copia così com'è senza sostituire niente. Da lì prende il
 * nome della propria cache, e cambiando versione il browser vede un file diverso e lo aggiorna.
 *
 * ## Se fallisce non succede niente, e si dice
 *
 * L'app funziona identica senza: il service worker aggiunge l'apertura senza rete, non una funzione.
 * Un errore qui non deve fermare l'avvio — ma non sparisce nemmeno in silenzio, perché una PWA che
 * non si installa mai e non lo dice è un'ora persa a chiedersi perché.
 */
function registerServiceWorker(): void {
  if (!shouldRegisterServiceWorker(location.protocol, location.hostname)) return;
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker
    .register(`sw.js?v=${encodeURIComponent(APP_VERSION)}`)
    .catch((error: unknown) => {
      console.warn('[palco] service worker non registrato:', error);
    });
}

registerServiceWorker();

// L'avvio è asincrono perché `device_id` e archivio lo sono: si aspetta, non si monta
// l'albero con un'identità che arriverà dopo.
void compose().then((composition) => {
  createRoot(container).render(
    <StrictMode>
      <App composition={composition} />
    </StrictMode>,
  );
}, showBootFailure);
